import { useState } from 'react'
import moment, { Moment } from 'moment-timezone'
import { DateSelectArg, EventClickArg } from '@fullcalendar/core'
import { DateClickArg } from '@fullcalendar/interaction'
import { EventCalendar } from '@/types/Calendar'
import { Daycare } from '@/types/Daycare'
import { getNextOpeningDate } from '@/modules/helper/daycareHelper'
import { PlanningTabId } from '@/hooks/usePlanningRights'
import { isWithinEditionLimit } from '@/modules/helper/editionLimitHelper'

interface DateRange {
    start: Moment
    end: Moment
}

interface DialogState<T> {
    isOpen: boolean
    data: T
}

interface PlanningDialogsState {
    regular: DialogState<DateRange>
    staff: DialogState<{ date: Moment }>
    showSlot: DialogState<{ event: EventCalendar | null }>
    exportCalendar: boolean
}

interface UsePlanningDialogsReturn {
    dialogs: PlanningDialogsState
    openRegularDialog: (start: Moment, end: Moment) => void
    openStaffDialog: (date: Moment) => void
    openShowSlotDialog: (event: EventCalendar) => void
    closeRegularDialog: () => void
    closeStaffDialog: () => void
    closeShowSlotDialog: () => void
    openExportDialog: () => void
    closeExportDialog: () => void
    handleSelectWeb: (info: DateSelectArg) => void
    handleSelectMobile: (info: DateClickArg) => void
    handleAddSlot: () => void
    handleEventClickArg: (info: EventClickArg) => void
    handleEventClick: (event: EventCalendar) => void
}

interface UsePlanningDialogsParams {
    currentDaycare: Daycare
    lastFromDate: Moment
    activeTabId: PlanningTabId
    openOccasionalDialog: (date: Moment) => void
}

const createInitialDateRange: () => DateRange = (): DateRange => ({
    start: moment().startOf('day'),
    end: moment().startOf('day')
})

export function usePlanningDialogs({
    currentDaycare,
    lastFromDate,
    activeTabId,
    openOccasionalDialog
}: UsePlanningDialogsParams): UsePlanningDialogsReturn {
    // Dialog states
    const [regularDialog, setRegularDialog] = useState<DialogState<DateRange>>({
        isOpen: false,
        data: createInitialDateRange()
    })

    const [staffDialog, setStaffDialog] = useState<DialogState<{ date: Moment }>>({
        isOpen: false,
        data: { date: moment().startOf('day') }
    })

    const [showSlotDialog, setShowSlotDialog] = useState<DialogState<{ event: EventCalendar | null }>>({
        isOpen: false,
        data: { event: null }
    })

    const [exportCalendar, setExportCalendar] = useState(false)

    // Open functions
    function openRegularDialog(start: Moment, end: Moment): void {
        setRegularDialog({ isOpen: true, data: { start, end } })
    }

    function openStaffDialog(date: Moment): void {
        setStaffDialog({ isOpen: true, data: { date } })
    }

    function openShowSlotDialog(event: EventCalendar): void {
        setShowSlotDialog({ isOpen: true, data: { event } })
    }

    // Close functions
    function closeRegularDialog(): void {
        setRegularDialog({ isOpen: false, data: createInitialDateRange() })
    }

    function closeStaffDialog(): void {
        setStaffDialog({ isOpen: false, data: { date: moment().startOf('day') } })
    }

    function closeShowSlotDialog(): void {
        setShowSlotDialog({ isOpen: false, data: { event: null } })
    }

    function openExportDialog(): void {
        setExportCalendar(true)
    }

    function closeExportDialog(): void {
        setExportCalendar(false)
    }

    // Handler functions
    function handleSelectWeb(info: DateSelectArg): void {
        if (!isWithinEditionLimit(info.start)) return

        const startDate: Moment = moment(info.start)
        const endDate: Moment = moment(info.end).subtract(1, 'd')
        const isSameDay: boolean = startDate.isSame(endDate)

        switch (activeTabId) {
            case PlanningTabId.REGULAR_TAB:
                openRegularDialog(startDate, isSameDay ? startDate : endDate)
                break
            case PlanningTabId.OCCASIONAL_TAB:
                openOccasionalDialog(startDate)
                break
            case PlanningTabId.STAFF_TAB:
                openStaffDialog(startDate)
                break
        }
    }

    function handleSelectMobile(info: DateClickArg): void {
        if (moment(info.date).isBefore(moment().startOf('day'))) return

        const date: Moment = moment(info.date)

        switch (activeTabId) {
            case PlanningTabId.REGULAR_TAB:
                openRegularDialog(date, date)
                break
            case PlanningTabId.OCCASIONAL_TAB:
                openOccasionalDialog(date)
                break
            case PlanningTabId.STAFF_TAB:
                openStaffDialog(date)
                break
        }
    }

    function handleAddSlot(): void {
        const dateToKeep: Moment = lastFromDate.isBefore(moment(), 'day') ? moment() : lastFromDate
        const date: Moment = getNextOpeningDate(currentDaycare, dateToKeep)

        switch (activeTabId) {
            case PlanningTabId.REGULAR_TAB:
                openRegularDialog(date, date)
                break
            case PlanningTabId.OCCASIONAL_TAB:
                openOccasionalDialog(date)
                break
            case PlanningTabId.STAFF_TAB:
                openStaffDialog(date)
                break
        }
    }

    function handleEventClickArg(info: EventClickArg): void {
        const event: EventCalendar = info.event.extendedProps as EventCalendar
        openShowSlotDialog({
            ...event,
            start: info.event.start ?? new Date(),
            end: info.event.end ?? new Date()
        })
    }

    function handleEventClick(event: EventCalendar): void {
        openShowSlotDialog(event)
    }

    return {
        dialogs: {
            regular: regularDialog,
            staff: staffDialog,
            showSlot: showSlotDialog,
            exportCalendar
        },
        openRegularDialog,
        openStaffDialog,
        openShowSlotDialog,
        closeRegularDialog,
        closeStaffDialog,
        closeShowSlotDialog,
        openExportDialog,
        closeExportDialog,
        handleSelectWeb,
        handleSelectMobile,
        handleAddSlot,
        handleEventClickArg,
        handleEventClick
    }
}
